import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { ApplicationUnverifiedLabel } from '../../../components/molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { StyledLock } from '../../../components/atoms/Lock/Lock';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { PrimaryButton } from '../../../components/atoms/Button/Button';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { ProviderEntity } from '../../../models/provider';
import { PageView } from '../../../models/analytics';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { Footer } from '../../../components/atoms/Footer/Footer';
import { messages } from '../../messages';
import { InstructionListHeader } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListHeader';
import { InstructionListBody } from '../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/InstructionList/InstructionListBody';
import { Header as HeaderImport, HeaderProps } from '../../../components/organisms/Header/Header';
import { usePageViewEvents } from '../../../core/Analytics/usePageViewEvents';
import { BackButton } from '../../../components/organisms/Header/HeaderMolecule';
import { SecurityCaption, SecurityText } from './ThirdPartyRedirectInstructionsScreen.styles';

export type ThirdPartyRedirectInstructionsScreenProps = {
  provider: ProviderEntity;
  clientName: string;
  isVerified: boolean;
  onClick: () => void;
} & BackButton;

export type DependentComponents = {
  Header?: React.ComponentType<HeaderProps>;
};

type ProviderDisplayNameParams = {
  providerName: string;
};

type GetInstructionsParams = {
  clientName: string | undefined;
  providerName: string;
};

const getInstructionsTitle = ({ providerName }: ProviderDisplayNameParams) => {
  return <FormattedMessage {...messages.title} values={{ providerName }} />;
};

const getInstructions = ({ providerName, clientName }: GetInstructionsParams) => {
  const isClientNameAbbreviated = clientName?.endsWith('.') ?? false;

  return [
    <FormattedMessage {...messages.redirectInstructionsStep1} values={{ providerName }} />,
    <FormattedMessage {...messages.redirectInstructionsStep2} />,
    <FormattedMessage
      {...messages.redirectInstructionsStep3}
      values={{ clientName, isAbbreviated: isClientNameAbbreviated }}
    />,
  ];
};

export const ThirdPartyRedirectInstructionsScreen = ({
  provider,
  clientName,
  isVerified,
  onClick,
  onBack,
  Header = HeaderImport,
}: ThirdPartyRedirectInstructionsScreenProps & DependentComponents) => {
  const pageView = PageView.THIRD_PARTY_REDIRECT_INSTRUCTIONS_SCREEN;
  usePageViewEvents({ pageView });

  const providerName = provider.displayName;

  const instructions = getInstructions({ providerName, clientName });

  const instructionsTitle = getInstructionsTitle({ providerName });

  return (
    <ScreenWrapper>
      <Header onBack={onBack} pageView={pageView} />
      <ContentBody>
        <Stack justifyContent="center">
          <InstructionListHeader title={instructionsTitle} provider={provider} />
          <InstructionListBody instructions={instructions} />
          <Box my="medium">
            <SecurityCaption>
              <StyledLock />
              <SecurityText>
                <FormattedMessage {...messages.securityCaption} />
              </SecurityText>
            </SecurityCaption>
          </Box>

          {!isVerified && (
            <Box my="medium">
              <ApplicationUnverifiedLabel isVerified={false} />
            </Box>
          )}
        </Stack>
        <Footer stickToBottom>
          <PrimaryButton pageView={pageView} variant="contained" color="primary" onClick={onClick}>
            <FormattedMessage
              {...messages.openProviderToLogIn}
              values={{ providerName: provider.displayName }}
            />
          </PrimaryButton>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};
